import * as React from 'react';
import { ChevronDown, Plus, X } from 'lucide-react';
import {
  Button,
  Input,
} from 'shared-portal-components-v2';

import type { ExternalInvitationManualValues } from '@/types/ExternalInvitationStep.types';
import { classNames } from '@/utils/classNames.utils';
import { MIN_LEN } from '@/constants';

export interface ExternalInvitationManualEntryRowProps {
  values: ExternalInvitationManualValues;
  onValuesChange: (nextValues: ExternalInvitationManualValues) => void;

  onAdd: () => void;
  onRemove: () => void;

  emailErrorMessage?: string;
  isAddDisabled?: boolean;

  className?: string;
}

interface PhoneCountryOption {
  code: string;
  dialCode: string;
  label: string;
  flag: string;
}

const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = [
  { code: 'US', dialCode: '+1', label: 'United States', flag: '🇺🇸' },
  { code: 'GB', dialCode: '+44', label: 'United Kingdom', flag: '🇬🇧' },
  { code: 'AU', dialCode: '+61', label: 'Australia', flag: '🇦🇺' },
  { code: 'DE', dialCode: '+49', label: 'Germany', flag: '🇩🇪' },
  { code: 'FR', dialCode: '+33', label: 'France', flag: '🇫🇷' },
  { code: 'IN', dialCode: '+91', label: 'India', flag: '🇮🇳' },
  { code: 'JP', dialCode: '+81', label: 'Japan', flag: '🇯🇵' },
  { code: 'MX', dialCode: '+52', label: 'Mexico', flag: '🇲🇽' },
  { code: 'BR', dialCode: '+55', label: 'Brazil', flag: '🇧🇷' },
];

const DEFAULT_COUNTRY_CODE = 'US';

function resolveCountryFromPhone(phoneNumber: string): PhoneCountryOption {
  const normalizedValue = phoneNumber.trim();
  const defaultCountry = PHONE_COUNTRY_OPTIONS.find((country) => country.code === DEFAULT_COUNTRY_CODE)
    ?? PHONE_COUNTRY_OPTIONS[0];

  if (!normalizedValue.startsWith('+')) {
    return defaultCountry;
  }

  const matchedCountry = [...PHONE_COUNTRY_OPTIONS]
    .sort((left, right) => right.dialCode.length - left.dialCode.length)
    .find((country) => normalizedValue.startsWith(country.dialCode));

  if (matchedCountry === undefined) {
    return defaultCountry;
  }

  return matchedCountry;
}

function applyCountryToPhone(phoneNumber: string, country: PhoneCountryOption): string {
  const trimmedPhone = phoneNumber.trim();
  const selectedCountry = resolveCountryFromPhone(trimmedPhone);

  let restValue = trimmedPhone;
  if (trimmedPhone.startsWith('+')) {
    restValue = trimmedPhone.slice(selectedCountry.dialCode.length).trimStart();
  }

  return restValue.length > MIN_LEN ? `${country.dialCode} ${restValue}` : country.dialCode;
}

function ExternalInvitationManualEntryRow(props: ExternalInvitationManualEntryRowProps): React.JSX.Element {
  const { values, onValuesChange, onAdd, onRemove, emailErrorMessage, isAddDisabled = false, className } = props;
  const countryMenuReference = React.useRef<HTMLDivElement | null>(null);
  const [isCountryMenuOpen, setIsCountryMenuOpen] = React.useState(false);

  const emailHasError = emailErrorMessage !== undefined && emailErrorMessage.length > MIN_LEN;
  const selectedCountry = React.useMemo(
    () => resolveCountryFromPhone(values.phoneNumber),
    [values.phoneNumber],
  );

  const selectedCountryCode = selectedCountry.code;
  const displayedPhoneNumber = values.phoneNumber.trim().length > MIN_LEN
    ? values.phoneNumber
    : selectedCountry.dialCode;

  React.useEffect(() => {
    if (!isCountryMenuOpen) {
      return;
    }

    function handlePointerDown(event: MouseEvent): void {
      if (event.target instanceof Node && countryMenuReference.current?.contains(event.target) === true) {
        return;
      }

      setIsCountryMenuOpen(false);
    }

    globalThis.document.addEventListener('mousedown', handlePointerDown);
    return () => {
      globalThis.document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isCountryMenuOpen]);

  const handleCountryChange = React.useCallback((nextCountryCode: string): void => {
    const nextCountry = PHONE_COUNTRY_OPTIONS.find((country) => country.code === nextCountryCode);
    if (nextCountry === undefined) {
      return;
    }

    onValuesChange({
      ...values,
      phoneNumber: applyCountryToPhone(values.phoneNumber, nextCountry),
    });
    setIsCountryMenuOpen(false);
  }, [onValuesChange, values]);

  return (
    <div className={classNames('space-y-2 overflow-visible', className)}>
      <div
        className={classNames(
          'grid gap-3 items-center',
          'grid-cols-1',
          'lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(0,1fr)_auto_auto]'
        )}
      >
        <Input
          value={values.name}
          onChange={(event: { currentTarget: { value: string; }; }) => {
            onValuesChange({ ...values, name: event.currentTarget.value });
          }}
          placeholder="Name"
          className="h-10 min-w-0"
        />

        <Input
          value={values.email}
          onChange={(event: { currentTarget: { value: string; }; }) => {
            onValuesChange({ ...values, email: event.currentTarget.value });
          }}
          placeholder="email@company.com"
          type="email"
          className={classNames('h-10 min-w-0', emailHasError ? 'border-destructive focus-visible:ring-destructive' : undefined)}
        />

        <div className="flex h-10 min-w-0 items-center gap-2 overflow-visible">
          <div ref={countryMenuReference} className="relative shrink-0">
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={isCountryMenuOpen}
              aria-label="Select country"
              onClick={() => {
                setIsCountryMenuOpen((previousValue) => !previousValue);
              }}
              className={classNames(
                'flex h-10 w-[72px] items-center justify-between rounded-xl border border-input bg-background px-3 text-sm shadow-xs',
                'hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              <span className="text-lg leading-none" aria-hidden>{selectedCountry.flag}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
            </button>

            {isCountryMenuOpen ? (
              <div
                role="listbox"
                className="absolute left-0 top-full z-30 mt-1 w-[220px] overflow-hidden rounded-xl border border-border bg-popover shadow-lg"
              >
                {PHONE_COUNTRY_OPTIONS.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    role="option"
                    aria-selected={country.code === selectedCountryCode}
                    onClick={() => {
                      handleCountryChange(country.code);
                    }}
                    className={classNames(
                      'flex w-full items-center justify-between px-3 py-2 text-left text-sm',
                      country.code === selectedCountryCode ? 'bg-accent/50' : 'hover:bg-accent/30'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span aria-hidden>{country.flag}</span>
                      <span>{country.label}</span>
                    </span>
                    <span className="text-muted-foreground">{country.dialCode}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="min-w-0 flex-1">
            <Input
              value={displayedPhoneNumber}
              onChange={(event: { currentTarget: { value: string; }; }) => {
                onValuesChange({ ...values, phoneNumber: event.currentTarget.value });
              }}
              placeholder={`${selectedCountry.dialCode} (555) 123-4567`}
              type="tel"
              className="h-10 w-full"
            />
          </div>
        </div>

        <Input
          value={values.organization}
          onChange={(event: { currentTarget: { value: string; }; }) => {
            onValuesChange({ ...values, organization: event.currentTarget.value });
          }}
          placeholder="Organization"
          className="h-10 min-w-0"
        />

        <Button
          type="button"
          onClick={onAdd}
          variant="primary"
          disabled={isAddDisabled}
          className={classNames(
            'h-10 px-4 rounded-xl whitespace-nowrap shrink-0 w-full lg:w-auto',
            'border-0'
          )}
        >
          <Plus className="h-4 w-4 shrink-0" />
          <span className="text-xs md:text-sm">Add to SIX</span>
        </Button>

        <Button
          type="button"
          variant="tertiary"
          size="icon"
          aria-label="Remove row"
          onClick={onRemove}
          className="h-10 w-10 rounded-xl text-muted-foreground hover:text-foreground shrink-0 justify-self-end"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {emailHasError ? (
        <p className="text-xs text-destructive flex items-center gap-1">
          <X className="h-3 w-3" />
          {emailErrorMessage}
        </p>
      ) : null}
    </div>
  );
}

ExternalInvitationManualEntryRow.displayName = 'ExternalInvitationManualEntryRow';

export default ExternalInvitationManualEntryRow;
